import { useContext } from 'react';
import SectionWrapper from './SectionWrapper';
import {
  Text,
  Box,
  Divider,
  ListItem,
  UnorderedList,
  FormControl,
  Select,
  FormErrorMessage,
  Collapse,
} from '@chakra-ui/react';
import TemplateContentField from './TemplateContentField';
import { useAppDispatch, useAppSelector } from '../../../store/redux/hooks';
import {
  addField,
  setLoyaltyBalance,
} from '../../../store/redux/slices/template.slice';
import SwitchField from './SwitchField';
import { CustomErrorIcon } from '../../../theme/icons/CustomErrorIcon';
import {
  FIELD_CONFIG,
  SLOT_LABELS,
  FRONT_FIELD_SECTIONS,
  FrontFieldSectionKey,
} from './utils/fieldVisibilityConfig';
import {
  useTemplateFieldValidation,
  useInitialDuplicateValidation,
} from './validation/useTemplateFieldValidation';
import AddMoreButton from './AddMoreButton';
import { MeContext } from '../../../context/MeContext';
import { MeContextState } from '../../../store/types/account';
import KeyFieldsSection from './KeyFieldsSection';

const FrontFields = ({ isEdit }: { isEdit: boolean }) => {
  const dispatch = useAppDispatch();
  const { state: meState } = useContext(MeContext) as { state: MeContextState };
  const expressivePassRolloutEnabled =
    meState?.expressivePassRolloutEnabled === true;
  const templateType = useAppSelector((s) => s.template.templateType);
  const loyaltyBalance = useAppSelector((s) => s.template.loyaltyBalance);
  const fields = useAppSelector((s) => s.template.fields);
  const config = FIELD_CONFIG[templateType];
  const { validateField, handleFocus } = useTemplateFieldValidation('fields');
  const errors = useAppSelector((s) => s.templateErrors);

  useInitialDuplicateValidation(
    [...FRONT_FIELD_SECTIONS],
    fields,
    validateField,
    expressivePassRolloutEnabled
  );

  if (expressivePassRolloutEnabled) {
    return <KeyFieldsSection isEdit={isEdit} />;
  }

  return (
    <SectionWrapper
      title="Fields"
      id="fields-section"
      tooltip={
        <Text textAlign="left">
          These are the fields shown on the front of your pass. Each field can
          include:
          <UnorderedList>
            <ListItem>a label that’s shown on the pass – optional</ListItem>
            <ListItem>
              a default value for static content that doesn’t change between
              users – optional
            </ListItem>
          </UnorderedList>
        </Text>
      }
      openOnLoad={true}
    >
      {FRONT_FIELD_SECTIONS.map((sectionKey, index) => {
        const section = fields[sectionKey];
        const limit = config[sectionKey].limit;
        const fieldEntries = Object.entries(section || {}).filter(
          ([, field]) => field !== null
        );
        const isLimitReached = fieldEntries.length >= limit;
        const minRequired = config[sectionKey].minRequired;
        const isMinRequiredReached = fieldEntries.length > minRequired;

        return (
          <Box key={sectionKey} mt={0}>
            {index > 0 && limit !== 0 && (
              <Divider borderColor="lightGrey" my={4} />
            )}
            {sectionKey === 'header' && templateType !== 'stampCard' && (
              <Box mb={2}>
                <FormControl
                  mb={loyaltyBalance.enabled ? 4 : 2}
                  isInvalid={
                    !!errors['loyaltyBalance.enabled'] ||
                    !!errors['loyaltyBalance.numberType']
                  }
                >
                  <SwitchField
                    value={loyaltyBalance.enabled}
                    onChange={(value: boolean) => {
                      dispatch(setLoyaltyBalance({ enabled: value }));
                      handleFocus('loyaltyBalance.enabled');
                    }}
                    label={
                      <Text fontSize="16px" fontWeight="500">
                        Enable loyalty balance
                      </Text>
                    }
                  />
                </FormControl>
                <Collapse in={loyaltyBalance.enabled}>
                  <FormControl
                    mt={2}
                    isInvalid={!!errors['loyaltyBalance.numberType']}
                  >
                    <Text fontSize="16px" fontWeight="500" mb={2}>
                      Balance number type
                    </Text>
                    <Select
                      value={loyaltyBalance.numberType}
                      onChange={(e) =>
                        dispatch(
                          setLoyaltyBalance({ numberType: e.target.value })
                        )
                      }
                      onFocus={() => handleFocus('loyaltyBalance.numberType')}
                      mb={4}
                      bg="#EEEEEE"
                      border="none"
                      borderRadius="md"
                      fontSize="14px"
                      size="sm"
                      placeholder="Select"
                    >
                      <option value="integer">Integer</option>
                      <option value="decimal">Decimal</option>
                    </Select>
                    {errors['loyaltyBalance.numberType'] && (
                      <FormErrorMessage
                        pl={2}
                        display="flex"
                        alignItems="center"
                        gap={1.5}
                      >
                        <CustomErrorIcon />
                        {errors['loyaltyBalance.numberType']}
                      </FormErrorMessage>
                    )}
                    {errors['loyaltyBalance.enabled'] && (
                      <FormErrorMessage
                        pl={2}
                        display="flex"
                        alignItems="center"
                        gap={1.5}
                      >
                        <CustomErrorIcon />
                        {errors['loyaltyBalance.enabled']}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                </Collapse>
                <Divider borderColor="lightGrey" my={4} />
              </Box>
            )}
            {fieldEntries.map(([slot, field], fieldIndex) => (
              <Box key={`${sectionKey}-${slot}`}>
                {fieldIndex > 0 && <Divider borderColor="lightGrey" my={4} />}
                <TemplateContentField
                  label={
                    templateType === 'stampCard' &&
                    sectionKey === 'header' &&
                    slot === 'one'
                      ? 'Stamp Field'
                      : loyaltyBalance.enabled &&
                          sectionKey === 'header' &&
                          slot === 'one'
                        ? 'Balance Field'
                        : `${sectionKey.charAt(0).toUpperCase() + sectionKey.slice(1)} Field ${SLOT_LABELS[slot] ?? slot}`
                  }
                  section={sectionKey as FrontFieldSectionKey}
                  slot={slot}
                  newlyAdded={field._newlyAdded}
                  validateField={validateField}
                  errors={errors}
                  isMinRequiredReached={isMinRequiredReached}
                  isEdit={isEdit}
                  autoGenerateFieldId
                />
              </Box>
            ))}

            {limit > 0 &&
              index === FRONT_FIELD_SECTIONS.length - 1 &&
              fieldEntries.length > 0 && (
                <Divider borderColor="lightGrey" my={4} />
              )}
            {limit > 0 && !isLimitReached && (
              <Box mt={4}>
                <AddMoreButton
                  onClick={() =>
                    dispatch(
                      addField({ section: sectionKey as FrontFieldSectionKey })
                    )
                  }
                  text={`Add ${sectionKey.toLowerCase()} field`}
                />
              </Box>
            )}
          </Box>
        );
      })}
    </SectionWrapper>
  );
};

export default FrontFields;
